import { useEffect, useRef, useState } from 'react';
import {
  simulationStreamUrl,
  type SimStreamStartedData,
  type SimStreamTestData,
  type SimulationRunSummaryDto,
  type SimVerdictName,
} from '../api/simulation';

// ============================================================================
// useSimulationStream — Phase 7 M4. The frontend's first SSE consumer.
//
// Opens an EventSource on the resumable simulation stream and folds its named
// events (queued / started / test.completed / test.failed / completed / failed)
// into live progress + verdict tallies. Native EventSource handles reconnection
// automatically and re-sends Last-Event-ID, so a dropped connection (or Azure
// Container Apps' ~240s idle cut) resumes without losing events — the backend
// replays the gap from the per-run seq'd buffer.
//
// Terminal handling: a terminating SSE stream would make native EventSource
// reconnect forever, so on the terminal `completed`/`failed` event we call
// close() explicitly to stop it.
// ============================================================================

export type SimPhase =
  | 'idle' | 'connecting' | 'queued' | 'running'
  | 'reconnecting' | 'complete' | 'failed' | 'error';

export interface SimTallies {
  Pass: number;
  Fail: number;
  Blocked: number;
  RefusedCorrectly: number;
  Error: number;
}

export interface SimStreamRecent extends SimStreamTestData {
  seq: number;
}

export interface SimStreamState {
  phase: SimPhase;
  totalTests: number;
  completed: number;
  tallies: SimTallies;
  recent: SimStreamRecent[];            // newest first, capped
  summary: SimulationRunSummaryDto | null;
  error: string | null;
}

const EMPTY_TALLIES: SimTallies = { Pass: 0, Fail: 0, Blocked: 0, RefusedCorrectly: 0, Error: 0 };
const MAX_RECENT = 60;

function initialState(seedTotal: number): SimStreamState {
  return {
    phase: 'connecting',
    totalTests: seedTotal,
    completed: 0,
    tallies: { ...EMPTY_TALLIES },
    recent: [],
    summary: null,
    error: null,
  };
}

/**
 * @param runId     the enqueued run's id, or null to stay idle
 * @param seedTotal totalTests known from the enqueue response (seeds the progress
 *                  bar before the first `started` event lands)
 */
export function useSimulationStream(runId: string | null, seedTotal = 0): SimStreamState {
  const [state, setState] = useState<SimStreamState>(
    runId ? initialState(seedTotal) : { ...initialState(0), phase: 'idle' },
  );

  // A terminal event has been seen — suppress the onerror-driven reconnect note.
  const doneRef = useRef(false);

  useEffect(() => {
    if (!runId) {
      setState({ ...initialState(0), phase: 'idle' });
      return;
    }

    doneRef.current = false;
    setState(initialState(seedTotal));

    const es = new EventSource(simulationStreamUrl(runId, 0));

    const seqOf = (e: MessageEvent) => {
      const n = Number(e.lastEventId);
      return Number.isFinite(n) ? n : 0;
    };

    es.addEventListener('queued', (e) => {
      const d = JSON.parse((e as MessageEvent).data) as { totalTests: number };
      setState((s) => ({ ...s, phase: 'queued', totalTests: d.totalTests || s.totalTests }));
    });

    es.addEventListener('started', (e) => {
      const d = JSON.parse((e as MessageEvent).data) as SimStreamStartedData;
      setState((s) => ({ ...s, phase: 'running', totalTests: d.totalTests || s.totalTests }));
    });

    const onTest = (e: Event) => {
      const me = e as MessageEvent;
      const d = JSON.parse(me.data) as SimStreamTestData;
      const bucket: SimVerdictName = d.verdict;
      setState((s) => ({
        ...s,
        phase: s.phase === 'complete' || s.phase === 'failed' ? s.phase : 'running',
        completed: s.completed + 1,
        tallies: { ...s.tallies, [bucket]: s.tallies[bucket] + 1 },
        recent: [{ ...d, seq: seqOf(me) }, ...s.recent].slice(0, MAX_RECENT),
      }));
    };
    es.addEventListener('test.completed', onTest);
    es.addEventListener('test.failed', onTest);

    es.addEventListener('completed', (e) => {
      const summary = JSON.parse((e as MessageEvent).data) as SimulationRunSummaryDto;
      doneRef.current = true;
      setState((s) => ({ ...s, phase: 'complete', summary, completed: summary.completedTests }));
      es.close(); // terminal — stop native auto-reconnect
    });

    es.addEventListener('failed', (e) => {
      const d = JSON.parse((e as MessageEvent).data) as { error?: string };
      doneRef.current = true;
      setState((s) => ({ ...s, phase: 'failed', error: d.error ?? 'Simulation failed.' }));
      es.close();
    });

    es.onopen = () => {
      setState((s) =>
        s.phase === 'connecting' || s.phase === 'reconnecting' ? { ...s, phase: 'running' } : s,
      );
    };

    es.onerror = () => {
      if (doneRef.current) return; // expected: we closed on the terminal event
      // Transient drop — EventSource is CONNECTING and will retry with Last-Event-ID.
      // If the browser gave up (CLOSED), surface a hard error.
      setState((s) => ({
        ...s,
        phase: es.readyState === EventSource.CLOSED ? 'error' : 'reconnecting',
        error: es.readyState === EventSource.CLOSED ? 'Stream closed unexpectedly.' : s.error,
      }));
    };

    return () => {
      doneRef.current = true; // prevent the teardown close from flipping phase to error
      es.close();
    };
  }, [runId, seedTotal]);

  return state;
}
